import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, Search, ExternalLink, Building2 } from "lucide-react";

export default function DRHPVault() {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const { data } = useQuery({
    queryKey: ["drhps", q],
    queryFn: async () => (await api.get(`/drhps${q ? `?q=${encodeURIComponent(q)}` : ""}`)).data,
  });
  const items = data?.data || [];
  return (
    <div className="px-4 sm:px-6 lg:px-12 py-8">
      <div className="flex items-center gap-3 mb-1">
        <FileText className="w-5 h-5 text-[#D91E2A]" />
        <span className="text-xs font-mono-fin uppercase tracking-[0.3em] text-[#D91E2A]">SEBI Filings</span>
      </div>
      <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight text-slate-900 mb-2">DRHP Vault</h1>
      <p className="text-slate-500 mb-6 max-w-2xl">Draft Red Herring Prospectuses filed with SEBI — key takeaways, risk factors, and objects of issue extracted for you.</p>

      <div className="relative max-w-md mb-8">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search by company or sector…"
          className="pl-9 bg-white border-slate-200 text-slate-900 placeholder:text-slate-500 font-mono-fin"
          data-testid="drhp-search-input"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map(d => (
          <div key={d.id} className="rounded-xl bg-white border border-slate-200 p-5 hover:border-[#D91E2A]/40 transition-all card-glow cursor-pointer" onClick={() => setSelected(d)} data-testid={`drhp-card-${d.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-[#D91E2A]/10 border border-[#D91E2A]/30 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#D91E2A]" />
              </div>
              <span className="text-[9px] font-mono-fin uppercase tracking-wider text-slate-500">{d.filed_date}</span>
            </div>
            <h3 className="font-heading font-bold text-lg text-slate-900 mb-1">{d.company_name}</h3>
            <div className="text-xs text-slate-500 font-mono-fin uppercase mb-3">{d.sector}</div>
            <div className="text-xs text-slate-500 line-clamp-2 mb-3">{d.summary}</div>
            <div className="flex items-center justify-between border-t border-slate-200 pt-3">
              <span className="font-mono-fin text-[#D91E2A] text-sm">{d.issue_size}</span>
              <span className="text-[10px] font-mono-fin uppercase text-[#D91E2A] flex items-center gap-1">View Details <ExternalLink className="w-3 h-3" /></span>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-slate-500 font-mono-fin py-10 text-center">No DRHP found.</div>}
      </div>

      <Dialog open={!!selected} onOpenChange={o => !o && setSelected(null)}>
        <DialogContent className="bg-white border border-slate-200 text-slate-900 max-w-3xl max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <div className="text-xs font-mono-fin uppercase tracking-widest text-[#D91E2A] mb-1">DRHP · {selected.sector}</div>
                <DialogTitle className="font-heading font-extrabold text-3xl tracking-tight text-slate-900">{selected.company_name}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-3 gap-4 mt-4">
                <Info label="Filed" value={selected.filed_date} />
                <Info label="Issue Size" value={selected.issue_size} tone="emerald" />
                <Info label="Promoters" value={selected.promoters} />
              </div>
              <div className="mt-6">
                <div className="text-xs font-mono-fin uppercase tracking-widest text-[#D91E2A] mb-2">Summary</div>
                <p className="text-sm text-slate-700 leading-relaxed">{selected.summary}</p>
              </div>
              <div className="mt-6">
                <div className="text-xs font-mono-fin uppercase tracking-widest text-[#D91E2A] mb-2">Objects of Issue</div>
                <ul className="space-y-1.5">
                  {(selected.objects_of_issue || []).map((o) => (
                    <li key={`obj-${o.slice(0, 40)}`} className="text-sm text-slate-700 flex gap-2"><span className="text-[#D91E2A] mt-0.5">▸</span>{o}</li>
                  ))}
                </ul>
              </div>
              <div className="mt-6">
                <div className="text-xs font-mono-fin uppercase tracking-widest text-rose-400 mb-2">Key Risks</div>
                <ul className="space-y-1.5">
                  {(selected.key_risks || []).map((o) => (
                    <li key={`risk-${o.slice(0, 40)}`} className="text-sm text-slate-700 flex gap-2"><span className="text-rose-400 mt-0.5">▸</span>{o}</li>
                  ))}
                </ul>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                {selected.pdf_url && (
                  <a href={selected.pdf_url} target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-md bg-[#D91E2A]/10 border border-[#D91E2A]/40 text-[#B01820] text-sm font-medium flex items-center gap-2" data-testid="drhp-view-pdf-button">
                    <FileText className="w-4 h-4" /> Open PDF
                  </a>
                )}
                {selected.sebi_link && (
                  <a href={selected.sebi_link} target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-md border border-slate-200 text-slate-700 text-sm font-medium flex items-center gap-2 hover:border-[#D91E2A]/40">
                    <ExternalLink className="w-4 h-4" /> SEBI Filing
                  </a>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value, tone }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
      <div className="text-[9px] font-mono-fin uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-sm ${tone === "emerald" ? "text-[#D91E2A] font-mono-fin" : "text-slate-900"}`}>{value || "-"}</div>
    </div>
  );
}
